import type { ChatProvider } from '@xsai-ext/shared-providers'

import { computed } from 'vue'

import { useCallStore } from '../stores/call'
import { useChatStore } from '../stores/chat'

interface CallSendOptions {
  providerId: string
  model: string
  chatProvider: ChatProvider
  providerConfig?: Record<string, unknown>
}

/**
 * AI 打电话功能的组合式函数
 * 提供发起通话、发送消息、结束通话等功能
 */
export function useCall() {
  const chatStore = useChatStore()
  const callStore = useCallStore()

  /**
   * 发起呼出通话
   * @param type 通话类型：'voice' 或 'video'
   * @param characterId 角色ID
   * @param characterName 角色名称
   * @param characterAvatar 角色头像URL（可选）
   * @param sendOptions 发送选项（model, chatProvider 等）
   */
  const initiateCall = async (
    type: 'voice' | 'video',
    characterId: string,
    characterName: string,
    characterAvatar?: string,
    sendOptions?: CallSendOptions,
  ) => {
    // 防止重复发起
    if (callStore.isCallActive || callStore.outgoingCallPending) {
      console.warn('Call already in progress or pending')
      return false
    }

    // 设置呼出等待状态
    callStore.setOutgoingCallPending(type, characterId, characterName, characterAvatar)

    // 向 AI 发送隐藏的系统消息，询问是否接听
    const callTypeText = type === 'voice' ? '语音' : '视频'
    const systemMessage = {
      role: 'system' as const,
      content: `[系统提示：用户正在向你发起${callTypeText}通话请求。请使用 answer_call 工具来回应：decision="accept"接听（message填写问候语）或"reject"拒绝（message填写拒绝理由）。]`,
      metadata: { type: 'call_system_prompt' },
    }

    try {
      // 先添加系统消息到聊天历史
      chatStore.messages = [...chatStore.messages, systemMessage as any]

      // 如果提供了 sendOptions，触发 AI 回复
      if (sendOptions) {
        await chatStore.send('', {
          ...sendOptions,
          skipUserMessage: true, // 跳过添加用户消息，因为是系统消息触发
        })
      }

      return true
    }
    catch (error) {
      console.error('Failed to initiate call:', error)
      callStore.cancelOutgoingCall()
      chatStore.setAiTypingState(false)
      return false
    }
  }

  /**
   * AI 拒绝通话
   * @param reason 拒绝原因
   */
  const rejectCall = (reason?: string) => {
    callStore.cancelOutgoingCall()

    // 可以在聊天记录中显示拒绝消息
    const rejectMessage = reason || '对方拒绝了通话'

    return rejectMessage
  }

  /**
   * 在通话中发送消息
   * @param text 消息内容
   * @param sendOptions 发送选项（可选）
   */
  const sendCallMessage = async (text: string, sendOptions?: CallSendOptions) => {
    if (!callStore.isCallActive) {
      console.warn('No active call')
      return false
    }

    if (!text.trim()) {
      return false
    }

    // 添加到通话转录（用户消息）
    const userMessage = {
      role: 'user' as const,
      content: text,
      timestamp: Date.now(),
    }
    callStore.addToTranscript(userMessage)

    // 发送给 AI（带通话标记）
    const callPrefix = callStore.callType === 'video' ? '视频通话' : '语音通话'
    const messageForAI = `[${callPrefix}]: ${text}`

    try {
      // 添加用户消息到历史
      chatStore.messages = [...chatStore.messages, {
        role: 'user',
        content: messageForAI,
        isHidden: true, // 标记为隐藏
      } as any]

      // 如果提供了 sendOptions，触发AI回复
      if (sendOptions) {
        console.info('📞 发送通话消息并等待AI回复')
        await chatStore.send('', {
          ...sendOptions,
          skipUserMessage: true, // 跳过添加用户消息（已经手动添加了）
        })
      }
      else {
        console.warn('⚠️ sendCallMessage: 未提供sendOptions，AI不会回复')
      }
      return true
    }
    catch (error) {
      console.error('Failed to send call message:', error)
      return false
    }
  }

  /**
   * 结束通话
   */
  const endCall = async () => {
    if (!callStore.isCallActive && !callStore.outgoingCallPending) {
      console.warn('No active call to end')
      return null
    }

    // 如果是呼出中但未接通，直接取消
    if (callStore.outgoingCallPending) {
      callStore.cancelOutgoingCall()

      // 通知 AI 用户取消了通话
      const cancelMessage = '[系统提示：用户取消了通话请求。]'
      try {
        chatStore.messages = [...chatStore.messages, {
          role: 'system',
          content: cancelMessage,
          metadata: { type: 'call_system' },
        } as any]
      }
      catch (error) {
        console.error('Failed to send cancel message:', error)
      }

      return null
    }

    // 结束通话并保存记录
    const callLog = callStore.endCall()

    if (callLog) {
      // 【关键】在聊天历史中添加可见的通话结束消息
      const endMessage = `通话结束，时长 ${callStore.formatDuration(callLog.duration)}`

      // 添加系统消息到聊天历史（用户可见）
      // 使用赋值触发 computed setter，确保 IndexedDB 持久化和 Vue 响应式更新
      chatStore.messages = [...chatStore.messages, {
        role: 'system',
        content: endMessage,
        // 添加特殊标记，用于识别这是通话记录
        metadata: {
          type: 'call_end',
          callType: callLog.type,
          duration: callLog.duration,
          startTime: callLog.startTime,
        },
      } as any]
    }

    // 无论是否生成 callLog，都确保关闭 typing 气泡
    chatStore.setAiTypingState(false)

    return callLog
  }

  /**
   * 接听来电
   */
  const acceptIncomingCall = async (characterAvatar?: string) => {
    const success = callStore.acceptIncomingCall(characterAvatar)

    if (success) {
      // 如果有 pendingGreeting，问候语会在 CallModal.vue 中直接使用，无需额外 AI 请求
      if (callStore.pendingGreeting) {
        console.info('📞 来电接听，pendingGreeting 已存在，跳过系统消息')
        return true
      }

      // 兜底：AI 未提供问候语时，通知 AI 用户接听了电话
      const callType = callStore.callType || 'voice'
      const callTypeText = callType === 'voice' ? '语音' : '视频'
      const acceptMessage = `[系统通知：用户接听了你的${callTypeText}通话请求！通话已建立，你应该先开口说第一句话。记住：你现在处于${callTypeText}通话中，请使用"dialogue"字段格式回复，自然地开始对话即可。]`
      try {
        chatStore.messages = [...chatStore.messages, {
          role: 'system',
          content: acceptMessage,
          metadata: { type: 'call_system' },
        } as any]
        return true
      }
      catch (error) {
        console.error('Failed to send accept message:', error)
        return false
      }
    }

    return false
  }

  /**
   * 拒绝来电
   */
  const rejectIncomingCall = async () => {
    callStore.rejectIncomingCall()

    // 通知 AI 用户拒绝了通话
    const rejectMessage = '[系统提示：用户拒绝了你的通话请求。]'
    try {
      chatStore.messages = [...chatStore.messages, {
        role: 'system',
        content: rejectMessage,
        metadata: { type: 'call_system' },
      } as any]
      return true
    }
    catch (error) {
      console.error('Failed to send reject message:', error)
      return false
    }
  }

  /**
   * 处理 AI 在通话中的回复，写入通话转录
   */
  const handleAICallResponse = (content: string) => {
    if (!callStore.isCallActive) {
      return false
    }
    if (!content || !content.trim()) {
      return false
    }
    callStore.addToTranscript({
      role: 'assistant',
      content,
      timestamp: Date.now(),
    })
    return true
  }

  /**
   * 恢复通话状态（页面刷新后）
   */
  const restoreCallState = () => {
    return callStore.restoreCallStateFromSession()
  }

  /**
   * 格式化通话时长
   */
  const formatDuration = (seconds: number) => {
    return callStore.formatDuration(seconds)
  }

  // 计算属性
  const isCallActive = computed(() => callStore.isCallActive)
  const callType = computed(() => callStore.callType)
  const outgoingCallPending = computed(() => callStore.outgoingCallPending)
  const incomingCallOffer = computed(() => callStore.incomingCallOffer)
  const currentCallDuration = computed(() => callStore.currentCallDuration)
  const currentTranscript = computed(() => callStore.currentTranscript)
  const callCharacterName = computed(() => callStore.callCharacterName)
  const callCharacterAvatar = computed(() => callStore.callCharacterAvatar)

  return {
    // 状态
    isCallActive,
    callType,
    outgoingCallPending,
    incomingCallOffer,
    currentCallDuration,
    currentTranscript,
    callCharacterName,
    callCharacterAvatar,

    // 方法
    initiateCall,
    rejectCall,
    sendCallMessage,
    endCall,
    acceptIncomingCall,
    rejectIncomingCall,
    handleAICallResponse,
    restoreCallState,
    formatDuration,
  }
}
